import { useEffect, useState } from 'react';
import { MenuItem, TextField, Typography } from '@mui/material';
import { FormDialog } from '../../../shared/components/Dialogs/FormDialog';
import type { EntityCardData } from './useEntityTypeEntities';

interface SelectEntityDialogProps {
  open: boolean;
  skillName: string;
  entities: EntityCardData[];
  onClose: () => void;
  onConfirm: (entityId: number) => void;
}

/**
 * Opened from an ENTITY-scope skill's "Run" button on the Main tab's
 * Analyses grid — that kind of skill researches one specific entity rather
 * than every entity of the type, so the user must pick which one before the
 * job is enqueued.
 */
export function SelectEntityDialog({ open, skillName, entities, onClose, onConfirm }: SelectEntityDialogProps) {
  const [entityId, setEntityId] = useState<number | ''>('');

  // Reset to the first entity each time the dialog opens for a (possibly
  // different) skill, rather than carrying over the previous selection.
  useEffect(() => {
    if (open) setEntityId(entities[0]?.id ?? '');
  }, [open, entities]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (entityId !== '') onConfirm(entityId);
  };

  return (
    <FormDialog
      open={open}
      title={`Run "${skillName}" on…`}
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel="Run"
      submitDisabled={entityId === '' || entities.length === 0}
      maxWidth="xs"
    >
      <Typography variant="body2" color="text.secondary">
        This analysis researches one entity at a time. Choose which one to run it on.
      </Typography>
      <TextField
        select
        label="Entity"
        value={entityId}
        onChange={(e) => setEntityId(Number(e.target.value))}
        fullWidth
        autoFocus
        disabled={entities.length === 0}
      >
        {entities.map((e) => (
          <MenuItem key={e.id} value={e.id}>
            {e.name}
          </MenuItem>
        ))}
      </TextField>
    </FormDialog>
  );
}
